import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader as Loader2, X } from 'lucide-react';
import { Portal } from '../Portal';

interface ChangelogModalProps {
  title: string | null;
  data: string;
  loading: boolean;
  onClose: () => void;
}

export const ChangelogModal: React.FC<ChangelogModalProps> = ({
  title,
  data,
  loading,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {title && (
        <Portal>
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 text-left">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={onClose}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-50 text-foreground"
            >
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold">Changelog</h3>
                  <p className="text-xs text-muted-foreground truncate">{title}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-border bg-background/50">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4">
                    <Loader2 className="w-10 h-10 animate-spin text-workspace-accent" />
                    <p className="text-sm font-medium text-muted-foreground">Fetching release notes...</p>
                  </div>
                ) : (
                  <>
                    <style>{`
                      .changelog-doc {
                        line-height: 1.7;
                        color: hsl(var(--foreground));
                      }
                      .changelog-doc h1, .changelog-doc h2, .changelog-doc h3 {
                        color: hsl(var(--foreground));
                        margin-bottom: 1rem;
                        font-weight: 700;
                      }
                      .changelog-doc h1 { font-size: 1.5rem; border-bottom: 1px solid hsl(var(--border)); padding-bottom: 0.5rem; margin-top: 2rem; }
                      .changelog-doc h2 { font-size: 1.25rem; margin-top: 1.5rem; color: var(--workspace-accent); }
                      .changelog-doc h3 { font-size: 1.1rem; margin-top: 1.25rem; }
                      .changelog-doc ul {
                        list-style-type: disc !important;
                        padding-left: 1.5rem !important;
                        margin-bottom: 1.25rem;
                        margin-top: 0.5rem;
                      }
                      .changelog-doc li { margin-bottom: 0.5rem; }
                      .changelog-doc p { margin-bottom: 1rem; color: hsl(var(--muted-foreground)); }
                      .changelog-doc code {
                        background: hsl(var(--muted));
                        padding: 0.2rem 0.4rem;
                        border-radius: 4px;
                        font-size: 0.9em;
                        color: var(--workspace-accent);
                        font-family: inherit;
                      }
                      .changelog-doc pre {
                        background: hsl(var(--muted)/50);
                        padding: 1rem;
                        border-radius: 8px;
                        overflow-x: auto;
                        margin: 1rem 0;
                        border: 1px solid hsl(var(--border));
                        white-space: pre-wrap;
                        word-break: break-all;
                        font-family: inherit;
                        font-size: 0.85rem;
                      }
                    `}</style>
                    <div
                      className="changelog-doc text-sm whitespace-normal"
                      dangerouslySetInnerHTML={{ __html: data }}
                    />
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-border flex justify-end bg-muted/30">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg font-bold transition-all shadow-lg active:scale-95"
                >
                  Close Release Notes
                </button>
              </div>
            </motion.div>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  );
};
